import { MagicContent, MagicHeader } from '@flik/shared/components';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getAction, hasAction } from '../actions/registry';
import {
  ActionContext,
  ActionData,
  ContentProps,
  HeaderProps,
} from '../actions/types';

interface MagicModalProps {
  initialHasResults?: boolean;
  onSignInClick?: () => void;
  showSignIn?: boolean;
  initialHeight?: number;
  initialAction?: string;
}

const MagicModal: React.FC<MagicModalProps> = ({
  initialHasResults = false,
  onSignInClick,
  showSignIn = false,
  initialHeight,
  initialAction = 'search',
}) => {
  // Magic-related state
  const [searchQuery, setSearchQuery] = useState('');
  const [badges, setBadges] = useState<string[]>([]);
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const [currentAction, setCurrentAction] = useState<string>(initialAction);
  const [shouldAutoFocus, setShouldAutoFocus] = useState(false);
  const [actionData, setActionData] = useState<ActionData | null>(null);
  const searchContentRef = useRef<any>(null);

  // Modal state
  const [isSticky, setIsSticky] = useState(initialHeight === undefined);
  const [originalTop, setOriginalTop] = useState<number | null>(null);
  const [hasResults, setHasResults] = useState(initialHasResults);

  // Calculate if content should be collapsed
  const isCollapsed = !hasResults && !searchQuery.trim() && badges.length === 0;

  // Clear selected result when search query changes
  useEffect(() => {
    setSelectedResultIndex(-1);
  }, [searchQuery]);

  // Initialize action data when current action changes
  useEffect(() => {
    if (currentAction !== 'search' && hasAction(currentAction)) {
      const actionComponent = getAction(currentAction);
      if (actionComponent.createDefaultData) {
        setActionData(actionComponent.createDefaultData(badges));
      }
    } else {
      setActionData(null);
    }
  }, [currentAction, badges]);

  // Auto-set hasResults to true when there's meaningful content to show
  useEffect(() => {
    // Only auto-show if we have content AND either search text or badges
    const hasMeaningfulContent =
      currentAction !== 'search' || searchQuery.trim() || badges.length > 0;

    if (hasMeaningfulContent && !hasResults) {
      console.log(
        'Auto-setting hasResults to true - meaningful content detected',
      );
      setHasResults(true);
    }
  }, [currentAction, hasResults, searchQuery, badges]);

  // Magic event handlers
  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (!showSignIn) {
        setSearchQuery('');
        setSelectedResultIndex(-1);
      }
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (searchContentRef.current) {
        e.preventDefault();
        if (e.key === 'ArrowDown') {
          searchContentRef.current.navigateDown();
        } else if (e.key === 'ArrowUp') {
          searchContentRef.current.navigateUp();
        }
      }
    } else if (e.key === 'Enter') {
      if (selectedResultIndex >= 0 && searchContentRef.current) {
        e.preventDefault();
        searchContentRef.current.selectCurrent();
      } else if (searchQuery.trim()) {
        e.preventDefault();
        const trimmedQuery = searchQuery.trim();
        if (trimmedQuery && !badges.includes(trimmedQuery)) {
          handleAddBadge(trimmedQuery);
        }
      }
    }
  };

  const handleDoubleClick = () => {
    // MagicModal will handle showing results when content is available
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleAddBadge = (badge: string) => {
    if (badge.trim() && !badges.includes(badge.trim())) {
      const trimmedBadge = badge.trim();
      setBadges([...badges, trimmedBadge]);
      setSearchQuery('');
    }
  };

  const handleRemoveBadge = (badgeToRemove: string) => {
    const newBadges = badges.filter((badge) => badge !== badgeToRemove);
    setBadges(newBadges);
  };

  const handleClearBadges = () => {
    setBadges([]);
  };

  const handleBackClick = () => {
    setCurrentAction('search');
  };

  const handleNavigateToAction = (actionKey: string) => {
    setCurrentAction(actionKey);
  };

  // Handle double-click on search header to show content
  const handleHeaderDoubleClick = () => {
    if (!hasResults) {
      console.log('Double-click detected - showing content');
      setHasResults(true);
    }
  };

  // Get the current action components
  const actionComponent = getAction(currentAction);

  // For action modes, check if they have content
  const isActionCollapsed = useMemo(() => {
    if (currentAction !== 'search') {
      return !actionComponent.content; // Collapsed if no content component
    }
    return false; // Search mode is handled by MagicModal
  }, [currentAction, actionComponent.content]);

  // Create data update handler
  const handleDataUpdate = useCallback((newData: ActionData) => {
    setActionData(newData);
  }, []);

  // Create action context
  const actionContext: ActionContext = {
    searchQuery,
    badges,
    hasSession: false,
    isLoggedIn: true,
    isLoading: false,
    onClearSearch: handleClearSearch,
    onAddBadge: handleAddBadge,
    onRemoveBadge: handleRemoveBadge,
    onClearBadges: handleClearBadges,
    onBackClick: handleBackClick,
    requestWindowResize: async () => {}, // Not needed for web
    signOut: async () => {},
  };

  // Create base header props
  const baseHeaderProps = {
    ...actionContext,
    isActionMode: currentAction !== 'search',
    actionTitle: currentAction === 'quit-app' ? 'Quit Flik' : undefined,
    onSignInClick: onSignInClick || (() => {}),
    onDoubleClick: handleDoubleClick,
    isCollapsed: isActionCollapsed,
  };

  // Create header props for non-search actions
  const headerProps: HeaderProps = {
    ...baseHeaderProps,
    data: actionData || ({} as ActionData),
    onDataUpdated: handleDataUpdate,
  };

  // Create theme change handler for web
  const handleThemeChanged = useCallback((theme: 'light' | 'dark') => {
    console.log('Theme changed in web app:', theme);
    // In the web app, we just log the theme change
    // The actual theme change is handled by the ThemeProvider
  }, []);

  // Create content props
  const contentProps: ContentProps = {
    ...actionContext,
    showSignIn,
    selectedResultIndex,
    setSelectedResultIndex,
    onKeyDown: handleKeyDown,
    onThemeChanged: handleThemeChanged,
    data: actionData || ({} as ActionData),
    onDataUpdated: handleDataUpdate,
  };

  // Render the header with unified container styling
  const HeaderComponent = actionComponent.header;
  let header: React.ReactNode;

  // For standard search, we need to pass additional props
  if (currentAction === 'search') {
    const StandardSearchHeader = actionComponent.header;
    header = (
      <MagicHeader>
        <StandardSearchHeader
          {...(baseHeaderProps as any)}
          onSearchChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          shouldAutoFocus={shouldAutoFocus}
          onAutoFocusComplete={() => setShouldAutoFocus(false)}
          applet={undefined}
        />
      </MagicHeader>
    );
  } else {
    header = (
      <MagicHeader>
        <HeaderComponent {...headerProps} />
      </MagicHeader>
    );
  }

  // Render the content if it exists with unified container styling
  let content: React.ReactNode = null;
  if (actionComponent.content) {
    const ContentComponent = actionComponent.content;
    content = (
      <MagicContent className="[&>div:first-child]:!p-[2px] [&>div:first-child]:!rounded-4xl [&>div:first-child>div]:!rounded-4xl">
        <ContentComponent
          {...contentProps}
          onNavigateToAction={handleNavigateToAction}
          {...(currentAction === 'search' ? { ref: searchContentRef } : {})}
        />
      </MagicContent>
    );
  }

  // Check if action component exists
  if (!actionComponent) {
    console.error(`Action "${currentAction}" not found in registry`);
    header = (
      <div className="text-center text-white">
        <div className="text-lg font-medium mb-2">Action Not Found</div>
        <div className="text-sm text-gray-300">
          The action "{currentAction}" is not available.
        </div>
      </div>
    );
    content = null;
  }

  useEffect(() => {
    // If no initialHeight is provided, always stay sticky at top
    if (initialHeight === undefined) {
      setIsSticky(true);
      return;
    }

    const handleScroll = () => {
      const scrollTop = window.scrollY;

      // Find the magic bar element and get its actual position
      const magicBarElement = document.querySelector('[data-magic-bar]');
      if (magicBarElement) {
        // If we don't have the original position yet, calculate it
        if (originalTop === null) {
          const rect = magicBarElement.getBoundingClientRect();
          const magicBarTop = rect.top + scrollTop;
          setOriginalTop(magicBarTop);
        }

        // Use the stored original position for sticky calculation
        const shouldBeSticky = originalTop !== null && scrollTop >= originalTop;
        setIsSticky(shouldBeSticky);
      } else {
        // Fallback to initialHeight threshold if element not found
        const shouldBeSticky = scrollTop >= initialHeight;
        setIsSticky(shouldBeSticky);
      }
    };

    // Call once to set initial state
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [originalTop, initialHeight]);

  // Handle click outside to collapse when no text
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Find the actual content container, not the full-width magic bar
      const contentContainer = document.querySelector('[data-magic-content]');
      console.log('Click outside detected:', {
        contentContainer: !!contentContainer,
        target: event.target,
        isCollapsed,
        hasInternalState: true,
      });

      if (
        contentContainer &&
        !contentContainer.contains(event.target as Node)
      ) {
        // Check if there's no search text and we're not collapsed
        const searchInput = contentContainer.querySelector('input');
        const hasText = searchInput ? searchInput.value.trim() : false;

        console.log('Click outside conditions:', {
          hasSearchInput: !!searchInput,
          hasText,
          isCollapsed,
          shouldCollapse: !hasText && !isCollapsed,
        });

        if (searchInput && !hasText && !isCollapsed) {
          console.log('Collapsing search content...');
          setHasResults(false);
        }
      }
    };

    if (!isCollapsed) {
      console.log('Adding click outside listener');
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      console.log('Not adding click outside listener - already collapsed');
    }

    return () => {
      console.log('Removing click outside listener');
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCollapsed]);

  return (
    <div
      data-magic-bar
      className={`${
        isSticky ? 'fixed top-0' : 'absolute'
      } left-0 right-0 z-50 pointer-events-none`}
      style={
        !isSticky && initialHeight !== undefined
          ? { top: `${initialHeight}px` }
          : {}
      }
    >
      <div className="pointer-events-none">
        <div
          className="max-w-4xl mx-auto px-8 pointer-events-auto"
          data-magic-content
        >
          {/* Magic Header - always visible */}
          <div className="pt-2" onDoubleClick={handleHeaderDoubleClick}>
            {header}
          </div>

          {/* Magic Content - only visible when not collapsed */}
          {!isCollapsed && content && <div className="pb-4">{content}</div>}
        </div>
      </div>
    </div>
  );
};

export default MagicModal;
